import { useEffect, type RefObject } from 'react';
import { focusableElements } from '@/hooks/useDialogFocusTrap';
import {
  beforeNextClick,
  lastInputKind,
  pressNowDown,
  pressReleasedLast,
} from '@/hooks/pointerPress';
import { escapeSpent, newestDialogOrder, nextOverlayOrder, spendEscape } from './overlayEscape';

const ITEM = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';

function itemsOf(menu: HTMLElement): HTMLElement[] {
  return Array.from(menu.querySelectorAll<HTMLElement>(ITEM)).filter(
    (item) => !item.hasAttribute('disabled') && !item.hidden,
  );
}

/*
  I-1681 (IU-3b K8e, `docs/concept-iphone-ux.md`): what reached the page last, a touch, a mouse or a
  key, and whether a press is down, are read from the app's one press tracker (`hooks/pointerPress`,
  T-0698), which watches from the first import, so the press that opens the first menu is already seen:
  a tap leaves the browser's hover where it touched, and a menu drawn under that point drew the item
  there as hovered (the table menu opened with Delete table lit). This file adds no document listener
  of its own for any of it.
*/

/** Marks a menu a finger or a pen opened; `index.css` draws no item highlighted in it. */
const OPENED_BY = 'data-menu-opened-by';

/**
 * Where Tab goes from inside an overlay drawn away from its trigger (a menu, a pop-out at the
 * document root): the stop after its trigger; Shift+Tab, the trigger.
 */
export function afterTheMenu(
  trigger: HTMLElement | null,
  menu: HTMLElement,
  backwards: boolean,
): HTMLElement | null {
  if (!trigger || backwards) return trigger;
  const stops = focusableElements(document.body).filter(
    (element) => !menu.contains(element) && (element.checkVisibility?.() ?? true),
  );
  const at = stops.indexOf(trigger);
  if (at !== -1) return stops[at + 1] ?? trigger;
  // A trigger outside the tab order (a tree row's ⋯ at `tabindex="-1"`, a tab under a context
  // menu): the first stop after it in the document, not the page's first stop.
  return (
    stops.find(
      (element) =>
        !trigger.contains(element) &&
        Boolean(trigger.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING),
    ) ?? trigger
  );
}

/**
 * A menu's keyboard, the one every `role="menu"` uses (F-0533, KD-9;
 * `docs/concept-web-keyboard-accessibility.md`): the WAI-ARIA menu button pattern.
 *
 * - **Open:** focus goes to the first item (else the first control), unless something inside
 *   already took it. *Custom view* opened with focus left on its trigger, so the next key went to
 *   the page.
 * - **Arrow Up / Down, Home, End:** move through the items, wrapping.
 * - **Tab:** closes it, focus on the stop after its trigger (Shift+Tab: the trigger); the menu does
 *   not stay open behind (the phone's record menu did, and the next Escape dropped focus to `body`).
 *   A press elsewhere stays the menu's own to answer, as it was.
 * - **Escape:** closes it, focus back on the trigger, the key spent so a dialog it sits in stays
 *   (KD-8); a dialog opened after the menu takes the key instead.
 *
 * `close` must set the menu closed without moving focus; the hook moves it.
 *
 * `focusOnOpen: false` leaves focus where it is on open (I-1616, IU-3b K8c): a menu a finger opened
 * from a text editor's toolbar, whose caret, and on a phone whose keyboard, must stay in the text.
 * Escape then closes it without moving focus to the trigger either.
 *
 * **On a touch** (I-1681, K8e): a menu a finger or a pen opened is marked `data-menu-opened-by="touch"`
 * and draws no item highlighted until a key is pressed in it; focus still goes in. A menu opened while
 * its press is still down (on `pointerdown`, a long press) ignores the click that ends that press, so
 * the opening tap never selects an item.
 */
export function useMenuKeyboard({
  open,
  close,
  menuRef,
  triggerRef,
  focusOnOpen = true,
}: {
  open: boolean;
  close: () => void;
  menuRef: RefObject<HTMLElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  focusOnOpen?: boolean;
}): void {
  useEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu) return undefined;
    const order = nextOverlayOrder();

    if (lastInputKind() === 'touch') menu.setAttribute(OPENED_BY, 'touch');
    const openingPress = pressNowDown();
    // The click that ends the opening press lands wherever the finger lifts, the menu now under it.
    const removeOpeningClick = openingPress
      ? beforeNextClick((event) => {
          // The opening press released and none since: gate 707, a menu opened by a right-click (which
          // sends no click) swallowed the next press's click, so its items did nothing.
          const ofOpeningPress = pressNowDown() === null && pressReleasedLast() === openingPress;
          if (ofOpeningPress && event.target instanceof Node && menu.contains(event.target)) {
            event.preventDefault();
            event.stopPropagation();
          }
        })
      : null;

    if (focusOnOpen && !menu.contains(document.activeElement)) {
      (itemsOf(menu)[0] ?? focusableElements(menu)[0])?.focus({ preventScroll: true });
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // A dialog opened after the menu takes it; a pop-out of the same control already spent it.
        if (escapeSpent(event) || newestDialogOrder() > order) return;
        event.preventDefault();
        spendEscape(event);
        const focusInMenu = menu.contains(document.activeElement);
        close();
        if (focusOnOpen || focusInMenu) triggerRef.current?.focus({ preventScroll: true });
        return;
      }
      if (!menu.contains(document.activeElement)) return;
      // A keyboard in use after the touch: what it focuses is drawn again.
      menu.removeAttribute(OPENED_BY);
      if (event.key === 'Tab') {
        // Moved here, not left to the browser: WebKit's Tab skips buttons, so from a menu item it
        // went nowhere and the menu stayed open (measured, WebKit at 390).
        event.preventDefault();
        close();
        afterTheMenu(triggerRef.current, menu, event.shiftKey)?.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      // Home and End in a field move its caret.
      const field = (document.activeElement as HTMLElement).closest(
        'input, textarea, [contenteditable]:not([contenteditable="false"])',
      );
      if (field && (event.key === 'Home' || event.key === 'End')) return;
      // The menu's keys stay the menu's: an arrow it had nowhere to go with went on to the page,
      // whose list moved its row and took focus out from under the open menu (Custom view with no
      // saved views, whose one control is a button). A menu without items walks its controls.
      event.preventDefault();
      event.stopPropagation();
      const menuItems = itemsOf(menu);
      const items = menuItems.length > 0 ? menuItems : focusableElements(menu);
      const at = items.indexOf(document.activeElement as HTMLElement);
      const step: Record<string, number | undefined> = { ArrowDown: 1, ArrowUp: -1 };
      let next: HTMLElement | undefined;
      if (event.key in step && at !== -1) {
        next = items[(at + (step[event.key] ?? 0) + items.length) % items.length];
      } else if (event.key === 'ArrowDown' && at === -1) {
        next = items[0];
      } else if (event.key === 'Home') {
        next = items[0];
      } else if (event.key === 'End') {
        next = items[items.length - 1];
      }
      next?.focus();
    };

    // Capture on `document`, as `useDismissable`: before the page's own keys and a dialog's trap.
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      removeOpeningClick?.();
      menu.removeAttribute(OPENED_BY);
    };
    // `close` is read fresh through the closure each open; re-running on a new closure would move
    // focus back to the first item mid-use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, menuRef, triggerRef, focusOnOpen]);
}
